import React, { useState, useRef, useEffect } from "react";
import axios from "axios";

const MetaForm = () => {
  const [inputText, setInputText] = useState("");
  const [groups, setGroups] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [relatedKeywords, setRelatedKeywords] = useState([]);
  const [selectedKeyword, setSelectedKeyword] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const groupRef = useRef(null);

  useEffect(() => {
    if (groups && groupRef.current) {
      groupRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [groups]);

  const generateGroups = async () => {
    setIsLoading(true);
    setError(null);

    const keywords = inputText
      .split(/[,\n]/)
      .map((kw) => kw.trim())
      .filter((kw) => kw);

    try {
      const response = await axios.post(
        "https://api.openai.com/v1/chat/completions",
        {
          model: "gpt-4o-mini",
          messages: [
            {
              role: "system",
              content:
                "You are an AI assistant specialized in keyword grouping and SEO optimization.",
            },
            {
              role: "user",
              content: `Group the following keywords into semantically related categories:

${keywords.join(", ")}

Guidelines:
1. Create 10-15 groups, or more if necessary.
2. Each group should have a descriptive name.
3. Include 7-20 keywords per group.
4. Ensure each keyword is used only once.
5. If a keyword doesn't fit any group, create a "Miscellaneous" group.
6. For each keyword, provide an estimated search volume (low, medium, high) and keyword difficulty (easy, medium, hard).

Present the result as a JSON object where keys are group names, and values are arrays of objects containing the keyword, estimated search volume, and keyword difficulty. Do not include any additional text or formatting.

Example format:
{
  "Group Name 1": [
    {"keyword": "keyword1", "volume": "high", "difficulty": "medium"},
    {"keyword": "keyword2", "volume": "low", "difficulty": "easy"},
    {"keyword": "keyword3", "volume": "medium", "difficulty": "hard"}
  ],
  "Group Name 2": [
    {"keyword": "keyword4", "volume": "medium", "difficulty": "easy"},
    {"keyword": "keyword5", "volume": "high", "difficulty": "hard"},
    {"keyword": "keyword6", "volume": "low", "difficulty": "medium"}
  ]
}`,
            },
          ],
        },
        {
          headers: {
            Authorization: `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
            "Content-Type": "application/json",
          },
        }
      );

      const content = response.data.choices[0].message.content;
      console.log("API Response:", content); // Log the raw API response

      let parsedGroups;
      try {
        parsedGroups = JSON.parse(content);
      } catch (parseError) {
        console.error("Error parsing JSON:", parseError);
        setError(
          `Failed to parse the generated groups. The API response might not be in the correct format. Please try again.`
        );
        setIsLoading(false);
        return;
      }

      if (typeof parsedGroups !== "object" || Array.isArray(parsedGroups)) {
        setError(
          `The generated groups are not in the expected format. Please try again.`
        );
        setIsLoading(false);
        return;
      }

      setGroups(parsedGroups);
    } catch (error) {
      console.error("Error grouping keywords:", error);
      if (error.response) {
        console.error("API Error Response:", error.response.data);
        setError(
          `Failed to generate groups. API Error: ${error.response.data.error.message}`
        );
      } else {
        setError(`Failed to generate groups. ${error.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const exportToCSV = () => {
    if (!groups) return;

    let csvContent = "Group Name,Keyword,Search Volume,Keyword Difficulty\n";

    Object.entries(groups).forEach(([groupName, keywords]) => {
      keywords.forEach((keywordObj) => {
        csvContent += `"${groupName}","${keywordObj.keyword}","${keywordObj.volume}","${keywordObj.difficulty}"\n`;
      });
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "keyword_groups_advanced.csv");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const suggestRelatedKeywords = async (keyword, groupName) => {
    setSelectedKeyword(keyword);
    setSelectedGroup(groupName);
    setIsLoading(true);
    setError(null);
    setRelatedKeywords([]);

    try {
      const response = await axios.post(
        "https://api.openai.com/v1/chat/completions",
        {
          model: "gpt-4o-mini",
          messages: [
            {
              role: "system",
              content:
                "You are an AI assistant specialized in SEO and keyword research.",
            },
            {
              role: "user",
              content: `Suggest 10-20 related keywords for: "${keyword}". Provide them as a comma-separated list.`,
            },
          ],
        },
        {
          headers: {
            Authorization: `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
            "Content-Type": "application/json",
          },
        }
      );

      const content = response.data.choices[0].message.content;
      console.log("Related Keywords API Response:", content);

      let suggestedKeywords = content.split(",").map((kw) => kw.trim());
      setRelatedKeywords(suggestedKeywords);
    } catch (error) {
      console.error("Error suggesting related keywords:", error);
      setError(`Failed to suggest related keywords. ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const closeRelatedKeywordsModal = () => {
    setSelectedKeyword(null);
    setSelectedGroup(null);
    setRelatedKeywords([]);
  };

  const addKeywordToGroup = (keyword) => {
    if (selectedGroup && groups) {
      setGroups((prevGroups) => ({
        ...prevGroups,
        [selectedGroup]: [
          ...prevGroups[selectedGroup],
          { keyword, volume: "medium", difficulty: "medium" },
        ],
      }));
      // Remove the added keyword from relatedKeywords
      setRelatedKeywords((prevKeywords) =>
        prevKeywords.filter((kw) => kw !== keyword)
      );
    }
  };

  const removeKeywordFromGroup = (groupName, keywordToRemove) => {
    setGroups((prevGroups) => ({
      ...prevGroups,
      [groupName]: prevGroups[groupName].filter(
        (keywordObj) => keywordObj.keyword !== keywordToRemove
      ),
    }));
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="card bg-base-100 shadow-xl mb-8">
        <div className="card-body">
          <h2 className="card-title text-2xl mb-4 text-primary">
            Advanced Keyword Grouping Tool
          </h2>
          <textarea
            className="textarea textarea-bordered w-full h-48 mb-4"
            placeholder="Enter your keywords here, separated by commas or new lines"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
          ></textarea>
          <div className="flex justify-center">
            <button
              className={`btn btn-primary ${isLoading ? "loading" : ""}`}
              onClick={generateGroups}
              disabled={isLoading || !inputText.trim()}
            >
              {isLoading ? "Grouping..." : "Group Keywords"}
            </button>
          </div>
          {error && <div className="alert alert-error mt-4">{error}</div>}
        </div>
      </div>

      {groups && (
        <div ref={groupRef} className="card bg-base-100 shadow-xl mb-8">
          <div className="card-body">
            <div className="flex justify-between items-center mb-6">
              <h2 className="card-title text-2xl text-primary">
                Keyword Groups
              </h2>
              <button
                className="btn btn-secondary btn-sm"
                onClick={exportToCSV}
              >
                Export to CSV
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Click on a keyword to see related suggestions. Click the group
              name to edit.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Object.entries(groups).map(([groupName, keywords]) => (
                <div
                  key={groupName}
                  className="card bg-neutral text-neutral-content shadow-md"
                >
                  <div className="card-body">
                    <h3
                      className="card-title text-lg mb-2 text-primary-content cursor-pointer hover:text-primary transition-colors duration-200"
                      onClick={() => {
                        /* Add edit group name functionality */
                      }}
                    >
                      {groupName}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {keywords.map((keywordObj, index) => (
                        <div key={index} className="relative group">
                          <span
                            className="badge badge-primary text-primary-content break-words text-xs cursor-pointer p-3 hover:bg-primary hover:text-primary-content transition-colors duration-200"
                            onClick={() =>
                              suggestRelatedKeywords(
                                keywordObj.keyword,
                                groupName
                              )
                            }
                          >
                            {keywordObj.keyword}
                          </span>
                          <div className="absolute left-0 top-full mt-1 p-2 bg-neutral text-neutral-content shadow-md rounded-md text-xs z-10 hidden group-hover:block">
                            <div className="font-semibold">
                              Volume:{" "}
                              <span className="font-normal">
                                {keywordObj.volume}
                              </span>
                            </div>
                            <div className="font-semibold">
                              Difficulty:{" "}
                              <span className="font-normal">
                                {keywordObj.difficulty}
                              </span>
                            </div>
                            <button
                              className="mt-2 text-error hover:text-error-content transition-colors duration-200"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeKeywordFromGroup(
                                  groupName,
                                  keywordObj.keyword
                                );
                              }}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {selectedKeyword && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-medium leading-6 text-gray-900 mb-2">
              Related Keywords for "{selectedKeyword}"
            </h3>
            {isLoading ? (
              <div className="flex justify-center items-center h-48">
                <span className="loading loading-spinner loading-lg"></span>
              </div>
            ) : error ? (
              <div className="alert alert-error">{error}</div>
            ) : (
              <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto">
                {relatedKeywords.map((keyword, index) => (
                  <span
                    key={index}
                    className="badge badge-secondary badge-outline break-words text-xs cursor-pointer pr-6 relative"
                    onClick={() => addKeywordToGroup(keyword)}
                  >
                    {keyword}
                    <span className="absolute right-1 top-1/2 transform -translate-y-1/2 text-xs font-bold">
                      +
                    </span>
                  </span>
                ))}
              </div>
            )}
            <div className="mt-4 flex justify-end">
              <button
                className="btn btn-primary"
                onClick={closeRelatedKeywordsModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MetaForm;
